import { useTranslation } from 'react-i18next';
import { IAlbum } from '@/data/models/album';
import {
  getLocaleCurrencyOrPlaceholder,
  getLocaleNumberOrPlaceholder,
  getValueOrPlaceholder,
  getVideoContentTypeDisplayName,
  parseDateString,
} from '@/utils/helper';
import {
  buildCopyrightLine,
  buildPhonographicCopyrightLine,
  formatGenre,
} from '@/components/common/ProductUtilities';
import { useProductDetailsTable } from '@/hooks/useProductDetailsTable';
import { usePriceTab } from '@/hooks/usePriceTab';

import styles from './ProductMetadataTable.module.scss';
import {
  LabelEntry,
  ParentLabelEntry,
  PreOrderLabel,
  ProductDetailsTableProps,
  ProductEntry,
  ProjectEntry,
  RequiredMessage,
  YesNo,
} from './ProductMetadataCommon';
import { AlbumState } from '@/data/models/albumState';

// --- Helper Functions ---
function formatPackaging(album: IAlbum) {
  const packagingDescription = album.albumMetaData?.packagingDescription || '';
  const packagingName = album.packaging?.name || '';
  return packagingDescription
    ? `${packagingName} (${packagingDescription})`
    : packagingName;
}

// --- Main Component ---

export default function ProductMetadataTable({
  errors,
  errorFlag,
  album,
  project,
  onToggleLabelInfoModal,
  onToggleParentLabelInfoModal,
  changeRequest,
}: ProductDetailsTableProps) {
  const { t } = useTranslation();
  const {
    canUserViewInternalFields,
    canUserReadProjectService,
    canUserAccessProjectServiceUi,
    hasRequiredError,
    isVideoProduct,
    albumCountryOfManufactureDisplayName,
  } = useProductDetailsTable({ errors, errorFlag, album });

  const { allRows } = usePriceTab(album, changeRequest);
  const primaryPriceRow = allRows.find(
    (row) => row.type === t('product.pricing.salesTerritoritoryTeam')
  );
  const primaryPriceDisplay =
    primaryPriceRow &&
    primaryPriceRow.name &&
    primaryPriceRow.price !== '' &&
    primaryPriceRow.price !== '--'
      ? `${primaryPriceRow.name} (${primaryPriceRow.currency}) ${primaryPriceRow.price}`
      : null;

  return (
    <div
      className={
        styles['product-details-grid'] +
        ' ' +
        ((album.albumMetaData?.versions.length ?? 0) > 1
          ? styles['product-details-multiple-versions']
          : '')
      }
    >
      {/* Start of grouping 1 */}

      <ProductEntry
        name={t('product.editProduct.title')}
        value={album.title}
        hasRequiredError={hasRequiredError('Title')}
        column={[1, 1]}
      />

      <ProductEntry
        name={t('product.editProduct.version')}
        value={getValueOrPlaceholder(album.albumMetaData?.versions[0])}
        column={[1, 1]}
      />

      {album.albumMetaData?.versions[1] !== undefined && (
        <ProductEntry
          name={t('product.editProduct.secondVersion')}
          value={getValueOrPlaceholder(album.albumMetaData?.versions[1])}
          column={[1, 1]}
        />
      )}

      {album.albumMetaData?.versions[2] !== undefined && (
        <ProductEntry
          name={t('product.editProduct.thirdVersion')}
          value={getValueOrPlaceholder(album.albumMetaData?.versions[2])}
          column={[1, 1]}
        />
      )}

      <LabelEntry
        album={album}
        canUserViewInternalFields={canUserViewInternalFields}
        onToggleLabelInfoModal={onToggleLabelInfoModal}
        hasRequiredError={hasRequiredError}
      />

      <ParentLabelEntry
        album={album}
        canUserViewInternalFields={canUserViewInternalFields}
        onToggleParentLabelInfoModal={onToggleParentLabelInfoModal}
        hasRequiredError={hasRequiredError}
      />

      <ProductEntry
        name={t('product.editProduct.GTIN')}
        value={getValueOrPlaceholder(album.gtin)}
        hasRequiredError={hasRequiredError('GTIN')}
        column={[1, 1]}
        redirectUrl={
          album.albumState === AlbumState.Released
            ? `/Physical/ProductDetails/Album/${album.id}`
            : null
        }
      />

      <ProductEntry
        name={t('product.editProduct.catalogNumber')}
        value={getValueOrPlaceholder(album.catalogNumber)}
        hasRequiredError={hasRequiredError('CatalogNumber')}
        column={[1, 1]}
      />

      <ProductEntry
        name={t('product.editProduct.genre')}
        value={getValueOrPlaceholder(formatGenre(album.albumMetaData?.genre))}
        hasRequiredError={hasRequiredError('Genre')}
        column={[1, 1]}
      />

      {/* End of grouping 1 */}

      {/* Start of grouping 2 */}

      <ProductEntry
        name={t('product.editProduct.releaseDate')}
        value={getValueOrPlaceholder(parseDateString(album.releaseDate))}
        hasRequiredError={hasRequiredError('ReleaseDate')}
        column={[1, 2]}
      />

      <ProductEntry
        name={t('product.editProduct.originalReleaseDate')}
        value={getValueOrPlaceholder(
          parseDateString(album?.originalReleaseDate)
        )}
        hasRequiredError={hasRequiredError('OriginalReleaseDate')}
        column={[1, 2]}
      />

      <ProductEntry
        name={t('product.editProduct.preOrderDate')}
        value={
          <PreOrderLabel
            enabled={album.albumMetaData?.retailPreOrder}
            date={album.albumMetaData?.retailPreOrderDate}
            time={album.albumMetaData?.retailPreOrderTime}
          />
        }
        hasRequiredError={hasRequiredError('RetailPreOrderDate')}
        column={[1, 2]}
      />

      <ProductEntry
        name={t('product.pricing.salesTerritoritoryTeam')}
        value={getValueOrPlaceholder(primaryPriceDisplay)}
        column={[2, 2]}
      />

      <ProductEntry
        name={t('product.editProduct.accountExclusiveRelease')}
        value={
          <>
            <YesNo value={album.albumMetaData?.exclusiveReleaseAccount} />{' '}
            {album.albumMetaData?.exclusiveReleaseAccount &&
              (hasRequiredError('ExclusiveReleaseAccountName') ? (
                <RequiredMessage withParenthesis />
              ) : (
                `(${album.albumMetaData?.exclusiveReleaseAccountName})`
              ))}
          </>
        }
        column={[2, 2]}
      />

      <ProductEntry
        name={t('product.editProduct.cline')}
        value={getValueOrPlaceholder(
          buildCopyrightLine(
            album.albumMetaData?.copyrightYear,
            album.albumMetaData?.copyrightLine
          )
        )}
        hasRequiredError={
          hasRequiredError('CopyrightYear') && hasRequiredError('CopyrightLine')
        }
        column={[2, 2]}
      />

      <ProductEntry
        name={t('product.editProduct.pline')}
        value={getValueOrPlaceholder(
          buildPhonographicCopyrightLine(
            album.albumMetaData?.phonographicCopyrightYear,
            album.albumMetaData?.phonographicCopyrightLine
          )
        )}
        hasRequiredError={
          hasRequiredError('PhonographicCopyrightYear') &&
          hasRequiredError('PhonographicCopyrightLine')
        }
        column={[2, 2]}
      />

      {/* End of grouping 2  */}

      {/* Start of grouping 3 */}

      {!isVideoProduct() && (
        <ProductEntry
          name={t('product.editProduct.releaseType')}
          value={getValueOrPlaceholder(album.albumMetaData?.releaseType)}
          hasRequiredError={hasRequiredError('ReleaseType')}
          column={[2, 3]}
        />
      )}

      {isVideoProduct() && (
        <ProductEntry
          name={t('product.editProduct.videoContentType')}
          value={getValueOrPlaceholder(
            getVideoContentTypeDisplayName(
              album.albumMetaData?.videoContentType,
              t
            )
          )}
          hasRequiredError={hasRequiredError('VideoContentType')}
          column={[2, 3]}
        />
      )}

      <ProductEntry
        name={t('product.editProduct.ingroovesManufacturingProduct')}
        value={
          <YesNo
            value={
              album.albumMetaData?.manufacturing?.isManufacturedByIngrooves
            }
          />
        }
        column={[2, 3]}
      />

      <ProductEntry
        name={t('product.editProduct.ingroovesCanadaRights')}
        value={
          <YesNo
            value={album.albumMetaData?.manufacturing?.ingroovesCanadaRight}
          />
        }
        column={[2, 3]}
      />

      <ProductEntry
        name={t('product.editProduct.countryOfManufacture')}
        value={getValueOrPlaceholder(albumCountryOfManufactureDisplayName)}
        hasRequiredError={hasRequiredError('CountryOfManufacture')}
        column={[2, 3]}
      />

      <ProductEntry
        name={t('product.editProduct.manufacturingCostPerUnit')}
        value={getLocaleCurrencyOrPlaceholder(
          album.albumMetaData?.manufacturing?.manufacturingCostPerUnit,
          album.albumMetaData?.manufacturing?.currency
        )}
        column={[2, 3]}
      />

      <ProductEntry
        name={t('product.editProduct.packaging')}
        value={getValueOrPlaceholder(formatPackaging(album))}
        hasRequiredError={hasRequiredError('Packaging')}
        column={[2, 3]}
      />

      <ProductEntry
        name={t('product.editProduct.projectedPhysicalIO')}
        value={getLocaleNumberOrPlaceholder(
          album.albumMetaData?.salesProjection?.projectedPhysicalIO
        )}
        hasRequiredError={hasRequiredError('ProjectedPhysicalIO')}
        column={[2, 3]}
      />

      <ProjectEntry
        project={project}
        canUserReadProjectService={canUserReadProjectService}
        canUserAccessProjectServiceUi={canUserAccessProjectServiceUi}
      />

      {/* End of grouping 3 */}
    </div>
  );
}
